import { reportError } from "utils/diagnostics";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogProps } from "@mui/material";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { PopupHeader } from "../popup/popup-header.component";
import { GhostButton, PrimaryButton } from "../Buttons";
import { showError } from "utils/notifications";
import style from "../popup/popup.module.css";

dayjs.extend(utc);

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const VALID_EXTENSIONS = [".tar.gz", ".tgz"];

interface PushConfigDialogProps extends Omit<DialogProps, "onClose"> {
  open: boolean;
  onClose: () => void;
  deviceCount: number;
  isSubmitting?: boolean;
  onConfirm: (configFileBase64: string, configFileName: string, applyNow: boolean, scheduleTime: string) => void;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function PushConfigDialog({
  open,
  onClose,
  deviceCount,
  isSubmitting = false,
  onConfirm,
}: PushConfigDialogProps): JSX.Element {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [updateOption, setUpdateOption] = useState<"now" | "later">("now");
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs().add(1, "hour"));
  const [selectedHour, setSelectedHour] = useState<number>(() => {
    const nextHour = dayjs().add(1, "hour").hour();
    return nextHour % 12 || 12;
  });
  const [selectedAmPm, setSelectedAmPm] = useState<"AM" | "PM">(() => {
    const nextHour = dayjs().add(1, "hour").hour();
    return nextHour >= 12 ? "PM" : "AM";
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const minDate = useMemo(() => dayjs().startOf("day"), []);
  const maxDate = useMemo(() => dayjs().add(7, "day").endOf("day"), []);

  const selectedDateTime = useMemo(() => {
    if (!selectedDate) return null;
    let hour24 = selectedHour;
    if (selectedAmPm === "PM" && selectedHour !== 12) {
      hour24 = selectedHour + 12;
    } else if (selectedAmPm === "AM" && selectedHour === 12) {
      hour24 = 0;
    }
    return selectedDate.hour(hour24).minute(0).second(0).millisecond(0);
  }, [selectedDate, selectedHour, selectedAmPm]);

  useEffect(() => {
    if (open) {
      const nextHour = dayjs().add(1, "hour");
      setSelectedDate(nextHour);
      const hour12 = nextHour.hour() % 12 || 12;
      setSelectedHour(hour12);
      setSelectedAmPm(nextHour.hour() >= 12 ? "PM" : "AM");
      setSelectedFile(null);
      setUpdateOption("now");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!VALID_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      showError("Please select a .tar.gz or .tgz file.");
      return;
    }
    if (file.size === 0) {
      showError("The selected file is empty.");
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      showError("File size must be under 5MB.");
      return;
    }

    setSelectedFile(file);
  };

  const handleConfirm = async () => {
    if (!selectedFile) {
      showError("Please upload a config archive.");
      return;
    }

    if (updateOption === "later") {
      if (!selectedDateTime) {
        showError("Please select a date and time for scheduling.");
        return;
      }
      const now = dayjs();
      if (selectedDateTime.isBefore(now)) {
        showError("Please select a future date and time.");
        return;
      }
      const diffDays = selectedDateTime.diff(now, "day", true);
      if (diffDays > 7) {
        showError("The scheduled time must be within 7 days from now.");
        return;
      }
    }

    try {
      const base64 = await fileToBase64(selectedFile);
      const scheduleTime =
        updateOption === "later" && selectedDateTime
          ? (selectedDateTime as any).utc().format("YYYY-MM-DD HH:mm:ss")
          : "";
      onConfirm(base64, selectedFile.name, updateOption === "now", scheduleTime);
    } catch (error) {
      reportError("config.file.handleConfirm", error);
      showError("Failed to read the selected file.");
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUpdateOption("now");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  return (
    <Dialog className={style.container} fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <PopupHeader title="Push Device Configuration" />
      <DialogContent>
        <div style={{ marginBottom: "24px" }}>
          <p style={{ margin: 0, fontSize: "16px" }}>
            Push configuration to {deviceCount} MegaFi 2 {deviceCount === 1 ? "device" : "devices"}.
          </p>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            style={{ textTransform: "none" }}
          >
            Upload config archive
            <input
              ref={fileInputRef}
              type="file"
              accept=".tar.gz,.tgz,application/gzip,application/x-gzip"
              hidden
              onChange={handleFileChange}
            />
          </Button>
          {selectedFile && (
            <p style={{ margin: "8px 0 0", fontSize: "14px", color: "#666" }}>{selectedFile.name} loaded.</p>
          )}
        </div>

        <FormControl component="fieldset" style={{ width: "100%", marginBottom: "24px" }}>
          <RadioGroup
            aria-label="push-option"
            name="push-option"
            value={updateOption}
            onChange={(event) => setUpdateOption(event.target.value as "now" | "later")}
          >
            <FormControlLabel value="now" control={<Radio />} label="Apply now" />
            <FormControlLabel value="later" control={<Radio />} label="Schedule for later time" />
          </RadioGroup>
        </FormControl>

        {updateOption === "later" && (
          <div style={{ marginBottom: "24px" }}>
            <div style={{ marginBottom: "16px" }}>
              <DatePicker
                label="Select date"
                value={selectedDate}
                onChange={(newValue: Dayjs | null) => setSelectedDate(newValue)}
                minDate={minDate}
                maxDate={maxDate}
                slots={{ textField: TextField }}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </div>
            <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
              <FormControl style={{ minWidth: "120px" }}>
                <InputLabel id="config-hour-label">Hour</InputLabel>
                <Select
                  labelId="config-hour-label"
                  id="config-hour-select"
                  value={selectedHour}
                  label="Hour"
                  onChange={(event: any) => setSelectedHour(Number(event.target.value))}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((hour) => (
                    <MenuItem key={hour} value={hour}>
                      {hour}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl style={{ minWidth: "120px" }}>
                <InputLabel id="config-ampm-label">AM/PM</InputLabel>
                <Select
                  labelId="config-ampm-label"
                  id="config-ampm-select"
                  value={selectedAmPm}
                  label="AM/PM"
                  onChange={(event: any) => setSelectedAmPm(event.target.value as "AM" | "PM")}
                >
                  <MenuItem value="AM">AM</MenuItem>
                  <MenuItem value="PM">PM</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
        )}

        <div style={{ marginBottom: "24px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
          <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
            <strong>Note:</strong> Please keep the devices ON to apply the config. The update automatically reboots the
            device. The device will go offline for approximately 5-10 mins.
          </p>
        </div>

        <div className={style.alignContentEvenly}>
          <div style={{ marginRight: "16px" }}>
            <GhostButton onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </GhostButton>
          </div>
          <PrimaryButton onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting ? "Pushing..." : "Push Config"}
          </PrimaryButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
